import { getBusinessUnitIdFromLocalStorage } from "helpers/storage";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { TablePaginationSizes, TABLE_PAGINATION_DEFAULT_OPTIONS } from "components/Common/Table/TablePagination";
import { useGetCurrentBusinessUnit, useOutsideClick } from "hooks";
import {
  FinancialTransaction,
  FinancialTransactionsQueryVariables,
  FinancialTransactionsSortByEnumType,
  useFinancialTransactionsLazyQuery,
} from "generated/graphql";
import { Flex } from "components/Structure/Flex";
import { LoadingOverlay } from "components";
import { Table } from "components/Common/Table";
import { TABLE_IDS } from "constants/Interface";
import { removeNothings } from "helpers/general/all";
import { Column } from "react-table";
import paginationStyles from "components/Common/Table/TablePagination/TablePagination.module.scss";
import cn from "classnames";
import { Icon } from "components";
import tablePaginationStyles from "components/Common/Table/TablePagination/TablePagination.module.scss";
import { getPageFrom, getPageTo } from "twComponents/PaginationControl/helpers";
import { RecordTypeLabel } from "../RecordTypeLabel";
import { TableOptionsContext } from "config/tableOptionsProvider";
import { getFormattedPrice, getFormattedDate } from "helpers/general";

export const FinancialTransactionsTable = () => {
  const businessUnitId = getBusinessUnitIdFromLocalStorage();
  const { currencyCodeISO } = useGetCurrentBusinessUnit();
  const { getTableOptions } = useContext(TableOptionsContext);

  const [firstCursor, setFirstCursor] = useState<null | string>(null);

  const [pageSize, setPageSize] = useState<TablePaginationSizes>(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNext, setHasNext] = useState<boolean | null>(null);
  const [hasPrev, setHasPrev] = useState<boolean | null>(null);

  const [pagination, setPagination] = useState<{
    last?: number;
    before?: string | null;
    first?: number;
    after?: string;
  }>({
    first: pageSize,
  });

  const handleChangePageSize = (size: TablePaginationSizes) => {
    setPageNumber(1);
    setPageSize(size);
    hidePageSizeOptionsMenu();
    setHasNext(null);
    setHasPrev(null);
    setPagination({
      first: size,
    });
  };

  const [getFinancialTransactions, { loading, data, refetch }] = useFinancialTransactionsLazyQuery({
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const [showPageSizeOptionsMenu, setShowPageSizeOptionsMenu] = useState<boolean>(false);

  const togglePageSizeOptionsMenu = () => {
    setShowPageSizeOptionsMenu((state) => !state);
  };
  const hidePageSizeOptionsMenu = () => {
    setShowPageSizeOptionsMenu(false);
  };

  const pageSizeMenuRef = useRef(null);
  useOutsideClick(pageSizeMenuRef, hidePageSizeOptionsMenu);

  const numberOfTransactions = data?.financialTransactions?.count ?? 0;
  const pageInfo = data?.financialTransactions?.pageInfo || undefined;
  const pageIndex = pageNumber - 1;
  const pageFrom = getPageFrom({ totalNumberOfItems: numberOfTransactions, pageSize, pageIndex });
  const pageTo = getPageTo({ totalNumberOfItems: numberOfTransactions, pageSize, pageFrom });

  const tableOptions = getTableOptions<Partial<FinancialTransaction>>(TABLE_IDS.FINANCIAL_TRANSACTIONS_TABLE);
  const sortBy = tableOptions?.sortBy ? tableOptions.sortBy[0] : { id: "date", desc: true };

  const canPrevPage = () => {
    if (hasPrev !== null) {
      return hasPrev;
    } else if (pageInfo?.hasPreviousPage !== null) {
      return pageInfo?.hasPreviousPage;
    } else {
      return false;
    }
  };

  const canNextPage = () => {
    if (hasNext !== null) {
      return hasNext;
    } else if (pageInfo?.hasNextPage !== null) {
      return pageInfo?.hasNextPage;
    } else {
      return false;
    }
  };

  useEffect(() => {
    const variables: FinancialTransactionsQueryVariables = {
      businessUnit: businessUnitId,
      orderBy: (sortBy?.id || "DATE") as FinancialTransactionsSortByEnumType,
      desc: sortBy?.desc !== undefined ? sortBy.desc : true,
      last: pagination.last,
      first: pagination.first,
      before: pagination.before,
      after: pagination.after,
    };
    getFinancialTransactions({ variables });
  }, [
    businessUnitId,
    sortBy?.id,
    sortBy?.desc,
    pagination.before,
    pagination.after,
    pagination.first,
    pagination.last,
    getFinancialTransactions,
  ]);

  useEffect(() => {
    if (pageInfo?.startCursor) {
      if (firstCursor == null && firstCursor != pageInfo.startCursor) {
        setFirstCursor(pageInfo.startCursor);
      }
    }
  }, [firstCursor, pageInfo]);

  const financialTransactions = useMemo(
    () =>
      removeNothings(
        loading
          ? getLoadingStateRows(pageSize)
          : data?.financialTransactions?.edges.map((transactionEdge) => {
              return transactionEdge?.node;
            }) || [],
      ),
    [data?.financialTransactions?.edges, loading, pageSize],
  );

  const columns: Column<Partial<FinancialTransaction>>[] = useMemo(
    () => [
      // the IDs here correspond to the string values of the sortBy enum
      // for the financial transactions query
      {
        id: "LABEL",
        Header: "Name",
        accessor: "label",
      },
      {
        id: "ANIMALS_COUNT",
        Header: "Number of Animals",
        accessor: "animalsCount",
      },
      {
        id: "AMOUNT",
        Header: "Amount",
        accessor: "amount",
        Cell: ({ row }) => <span>{getFormattedPrice(row.original.amount, currencyCodeISO)}</span>,
      },
      {
        id: "DATE",
        Header: "Date",
        accessor: "date",
        Cell: ({ row }) => <span>{getFormattedDate(row.original.date)}</span>,
      },
      {
        id: "RECORD_TYPE",
        Header: "Type",
        accessor: "recordType",
        Cell: ({ row }) => <RecordTypeLabel recordType={row.original.recordType} />,
      },
    ],
    [currencyCodeISO],
  );

  return (
    <Flex container item>
      {loading ? <LoadingOverlay /> : null}
      <TableControls
        canPrevPage={canPrevPage()}
        canNextPage={canNextPage()}
        setPageNumber={setPageNumber}
        refetch={refetch}
        pageSize={pageSize}
        businessUnitId={businessUnitId}
        setHasNext={setHasNext}
        setHasPrev={setHasPrev}
        pageInfo={pageInfo}
        pageSizeMenuRef={pageSizeMenuRef}
        showPageSizeOptionsMenu={showPageSizeOptionsMenu}
        pageFrom={pageFrom}
        pageTo={pageTo}
        handleChangePageSize={handleChangePageSize}
        numberOfTransactions={numberOfTransactions}
        togglePageSizeOptionsMenu={togglePageSizeOptionsMenu}
      />
      <Table<Partial<FinancialTransaction>>
        loading={loading}
        pagination
        initialPageSize={100}
        tableId={TABLE_IDS.FINANCIAL_TRANSACTIONS_TABLE}
        columns={columns}
        data={financialTransactions}
      />
    </Flex>
  );
};

export const getLoadingStateRows = (numberOfRows: TablePaginationSizes) => {
  const financialTransactionRow: Partial<FinancialTransaction> = {
    id: "",
    label: "-",
    animalsCount: 0,
    amount: 0,
    date: "-",
    recordType: undefined,
  };

  return Array.from({ length: numberOfRows }, () => financialTransactionRow);
};

const TableControls = ({
  canPrevPage,
  canNextPage,
  setHasNext,
  setHasPrev,
  setPageNumber,
  refetch,
  pageSize,
  businessUnitId,
  pageInfo,
  pageSizeMenuRef,
  showPageSizeOptionsMenu,
  togglePageSizeOptionsMenu,
  pageFrom,
  pageTo,
  handleChangePageSize,
  numberOfTransactions,
}) => {
  return (
    <div style={{ flex: 1 }}>
      <Flex
        className={tablePaginationStyles.table_pagination_target}
        container
        containerAlignItems="center"
        containerJustifyContent="flex-end"
        containerWrap="wrap"
      >
        <Flex item itemGrow />
        <>
          <Flex item container>
            <button
              className={cn(
                paginationStyles.table_pagination__button,
                paginationStyles["table_pagination__button--first"],
              )}
              disabled={!canPrevPage}
              onClick={() => {
                setPageNumber(1);
                setHasPrev(null);
                refetch({
                  first: pageSize,
                  after: undefined,
                  last: undefined,
                  before: undefined,
                  businessUnit: businessUnitId,
                });
              }}
              title={`First page`}
            >
              <Icon name="doubleChevron" rotate={180} size="atom" />
            </button>

            <button
              className={cn(
                paginationStyles.table_pagination__button,
                paginationStyles["table_pagination__button--prev"],
              )}
              title="Previous page"
              disabled={!canPrevPage}
              onClick={() => {
                setHasNext(true);
                setHasPrev(null);
                refetch({
                  first: undefined,
                  after: undefined,
                  last: pageSize,
                  before: pageInfo?.startCursor,
                  businessUnit: businessUnitId,
                });
                setPageNumber((pageNumber) => pageNumber - 1);
              }}
            >
              <Icon name="chevron" rotate={180} size="atom" />
            </button>
          </Flex>

          <Flex item>
            <>
              <div className={paginationStyles.table_pagination__per_page} ref={pageSizeMenuRef}>
                <button
                  className={cn(paginationStyles.table_pagination__current_page, {
                    [paginationStyles["table_pagination__current_page--active"]]: showPageSizeOptionsMenu,
                  })}
                  type="button"
                  onClick={togglePageSizeOptionsMenu}
                  title="Rows per page"
                >
                  <Flex container>
                    <Flex item itemGutter>
                      {pageFrom} to {pageTo}
                    </Flex>
                    <Flex item>
                      <Icon name="rectangle" size="atom" colour="grey_dark" />
                    </Flex>
                  </Flex>
                </button>

                {showPageSizeOptionsMenu ? (
                  <div className={paginationStyles.table_pagination__page_sizes}>
                    <span className={paginationStyles.table_pagination__page_sizes_title}>Rows:</span>

                    {TABLE_PAGINATION_DEFAULT_OPTIONS.SIZES.map((size) => (
                      <span
                        className={cn(paginationStyles.table_pagination__page_size, {
                          [paginationStyles["table_pagination__page_size--active"]]: size === pageSize,
                        })}
                        key={size}
                        onClick={() => handleChangePageSize(size)}
                        onKeyUp={() => handleChangePageSize(size)}
                        tabIndex={0}
                      >
                        {size}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>

              <span className={paginationStyles.table_pagination__total_items}>of {numberOfTransactions}</span>
            </>
          </Flex>

          <Flex item container>
            <button
              title="Next page"
              disabled={!canNextPage}
              onClick={() => {
                setHasPrev(true);
                setHasNext(null);
                refetch({
                  first: pageSize,
                  after: pageInfo?.endCursor,
                  last: undefined,
                  before: undefined,
                  businessUnit: businessUnitId,
                });
                setPageNumber((pageNumber) => pageNumber + 1);
              }}
              className={cn(
                paginationStyles.table_pagination__button,
                paginationStyles["table_pagination__button--next"],
              )}
            >
              <Icon name="chevron" size="atom" />
            </button>
            <button
              className={cn(
                paginationStyles.table_pagination__button,
                paginationStyles["table_pagination__button--last"],
              )}
              disabled={!canNextPage}
              onClick={() => {
                setHasNext(null);
                refetch({
                  first: undefined,
                  after: undefined,
                  last: numberOfTransactions % pageSize || pageSize,
                  before: undefined,
                  businessUnit: businessUnitId,
                });
                setPageNumber(Math.ceil(numberOfTransactions / pageSize));
              }}
              title="Last page"
            >
              <Icon name="doubleChevron" size="atom" />
            </button>
          </Flex>
        </>
      </Flex>

      <div style={{ width: 30, height: 16 }} />
    </div>
  );
};
